/**
 * Warning copy for a calendar write that only PARTLY succeeded
 * (`partial_success` on POST /api/oauth/calendar/events).
 *
 * Two surfaces show it — `useCreateCalendarEvent` (hooks/useCalendar.js) for
 * the Calendar page, and MeetingActionModal's Schedule tab — and each used to
 * build the `outlook_permanent ? no-retry : retry` sentence itself, with
 * different wording (#514 audit A10). The two nouns genuinely differ (the
 * Calendar page created an *event*; the Schedule tab scheduled a *meeting*
 * and already names the provider in its own footer), but the PERMANENCE
 * decision must not: a permanent Graph rejection (#514 F-2) enqueues no
 * server-side retry, so no copy may promise one. That decision lives here,
 * once.
 *
 * This lives in lib/ rather than components/schedule/ because a hook imports
 * it, and no hook in this codebase imports from components/.
 */

/** Per-surface copy. Only the noun differs; the permanence branch is shared. */
const WARNING_COPY = {
    event: {
        // "Meeting", matching this surface's success toast — the Calendar page
        // schedules meetings, and three nouns for one action was the #514
        // audit's A21.
        permanent: (provider) => `Meeting scheduled, but ${provider} rejected it and will not retry.`,
        transient: (provider) => `Meeting scheduled, but ${provider} sync failed and will retry automatically.`,
    },
    meeting: {
        // The Schedule tab writes to exactly ONE calendar the user picked, and
        // its footer already names that calendar — so the copy stays
        // provider-light and talks about the invitation, not a sync.
        permanent: () => 'Meeting saved, but Outlook rejected the invitation and will not retry.',
        transient: () => 'Meeting saved, but the invitation could not be sent. It will retry automatically.',
    },
};

/**
 * @param {object|null|undefined} data POST /oauth/calendar/events response body
 * @param {{variant?: 'event'|'meeting', preferServerCopy?: boolean}} [opts]
 *   `variant` picks the noun. `preferServerCopy` shows `data.warning` verbatim
 *   when the backend sent one (it is already permanence-aware); defaults on
 *   for the 'event' variant, which is the fan-out path the server writes that
 *   copy for.
 * @returns {string} the warning to toast
 */
export function calendarWriteWarning(data, { variant = 'event', preferServerCopy = variant === 'event' } = {}) {
    const body = data || {};
    if (preferServerCopy && body.warning) return body.warning;
    const provider = body.google_failed ? 'Google Calendar'
        : body.outlook_failed ? 'Outlook Calendar'
        : 'one calendar';
    const copy = WARNING_COPY[variant] || WARNING_COPY.event;
    // THE permanence decision — one branch for every surface (#514 F-2).
    return body.outlook_permanent ? copy.permanent(provider) : copy.transient(provider);
}


/**
 * Guest-facing note for a booking whose calendar write did not fully succeed.
 *
 * `calendar_error` on a booking is one of the backend's sentinels —
 * `teams_refused`, `calendar_not_connected`, `calendar_write_failed`,
 * `conference_unavailable_on_host` — or, on the Google branch, a raw exception
 * string. The guest can act on none of that, so nothing internal reaches the
 * page: no provider names, no status codes, no raw sentinel. The one thing
 * they need is that the time is booked and the details are coming.
 *
 * Shared by PublicBooking, BookingWidget and PublicReschedule so a guest gets
 * the same explanation wherever the booking surfaced (#514 audit A09).
 *
 * @param {string} reason the booking's `calendar_error`
 * @param {string|null|undefined} organiserName shown as "who will follow up"
 * @param {{hasMeetingLink?: boolean}} [opts] pass `!!booking.meet_link` (#755)
 */
export function calendarErrorNotice(reason, organiserName, { hasMeetingLink = false } = {}) {
    const who = organiserName || 'The organiser';
    // #755: the booking may still carry a meeting link — the coach's pasted
    // static link stands in when none could be minted. The page then shows a
    // Join button, so the copy must not say the link is missing; only the
    // invitation is. `conference_unavailable_on_host` is the exception: the
    // provider event WAS created (carrying the static link as its location) and
    // its invitation sent — only the minted conference was missing.
    if (hasMeetingLink) {
        if (reason === 'conference_unavailable_on_host') {
            return 'Your time is booked and your meeting link is ready. A calendar invite is on its way to your inbox.';
        }
        return "Your time is booked and your meeting link is ready, but a calendar invitation couldn't be sent automatically.";
    }
    if (reason === 'teams_refused' || reason === 'conference_unavailable_on_host') {
        return `Your time is booked, but we couldn't add a meeting link automatically. ${who} will follow up with the joining details.`;
    }
    // calendar_not_connected, calendar_write_failed, and anything else the
    // backend puts here (it can carry a raw provider error) share one calm
    // fallback — never the sentinel itself.
    return `Your time is booked, but a calendar invitation couldn't be sent automatically. ${who} will follow up with the meeting details.`;
}
